// src/utils/metrics.js
// Core analytics engine for meeting report generation

import {
  extractCodes, parseQuotes, speakingTimePerPerson,
  countGeniusPerSpeaker, ALL_CODES, BACKTICK_CODES, PROCESS_CODE_PAIRS, formatDuration
} from './shortcodes';

// ─── PARTICIPATION ────────────────────────────────────────────────────────────

export function computeParticipation(entries) {
  const times = speakingTimePerPerson(entries);
  const totalMs = times.reduce((a, b) => a + b.ms, 0);
  const n = times.length;

  const withPct = times.map(p => ({
    ...p,
    pct: totalMs ? Math.round((p.ms / totalMs) * 1000) / 10 : 0,
  }));

  // Dominance index: how much the top speaker dominates (0–1)
  const dominanceIndex = n > 0 && totalMs
    ? Math.round((times[0]?.ms / totalMs) * 100) / 100
    : 0;

  // Participation balance: 1 = perfectly equal, 0 = one person spoke everything
  const ideal = totalMs / Math.max(n, 1);
  const deviationSum = times.reduce((sum, p) => sum + Math.abs(p.ms - ideal), 0);
  const balanceIndex = totalMs
    ? Math.round((1 - deviationSum / (2 * totalMs)) * 100) / 100
    : 1;

  // Silence index: gaps between entries as % of total elapsed
  let silenceMs = 0;
  const sorted = [...entries].sort((a, b) => (a.startTime || 0) - (b.startTime || 0));
  for (let i = 1; i < sorted.length; i++) {
    const gap = (sorted[i].startTime || 0) - (sorted[i - 1].endTime || 0);
    if (gap > 2000) silenceMs += gap;
  }
  const elapsed = sorted.length > 1
    ? (sorted[sorted.length - 1].endTime || 0) - (sorted[0].startTime || 0)
    : 0;
  const silenceIndex = elapsed
    ? Math.round((silenceMs / elapsed) * 100) / 100
    : 0;

  return {
    speakers: withPct,
    totalMs,
    dominanceIndex,
    balanceIndex,
    silenceIndex,
    uniqueSpeakers: n,
  };
}

// ─── BREVITY ──────────────────────────────────────────────────────────────────

export function computeBrevity(entries) {
  const timed = entries.filter(
    e => e.type !== 'break' && e.startTime && e.endTime && e.endTime > e.startTime
  );
  if (timed.length === 0) return null;

  const durations = timed.map(e => e.endTime - e.startTime);

  const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
  const sortedD = [...durations].sort((a, b) => a - b);
  const median = sortedD[Math.floor(sortedD.length / 2)];

  const longWindedCount = durations.filter(d => d > median * 2).length;
  const longWindedPct = Math.round((longWindedCount / durations.length) * 100);

  const curtCount = durations.filter(d => d < median * 0.25).length;
  const curtPct = Math.round((curtCount / durations.length) * 100);

  const maxIdx = durations.indexOf(Math.max(...durations));
  const longestEntry = timed[maxIdx];

  return {
    avgMs: Math.round(avg),
    medianMs: Math.round(median),
    longWindedCount,
    longWindedPct,
    curtCount,
    curtPct,
    longestEntry: longestEntry || null,
    longestMs: Math.max(...durations),
  };
}

// ─── AWARDS ───────────────────────────────────────────────────────────────────

export function computeAwards(entries) {
  const sorted = [...entries].sort((a, b) => (a.startTime || 0) - (b.startTime || 0));

  const longestComments = entries
    .filter(e => e.startTime && e.endTime && e.endTime > e.startTime && e.speakerName)
    .map(e => ({ speaker: e.speakerName, ms: e.endTime - e.startTime, text: e.text }))
    .sort((a, b) => b.ms - a.ms)
    .slice(0, 3);

  const speakerEntries = {};
  sorted.forEach(e => {
    if (!e.startTime || !e.endTime || e.endTime <= e.startTime) return;
    if (!e.speakerName) return;
    if (!speakerEntries[e.speakerName]) speakerEntries[e.speakerName] = [];
    speakerEntries[e.speakerName].push(e);
  });
  const longestMutes = Object.entries(speakerEntries)
    .map(([name, evts]) => {
      let maxGap = 0;
      for (let i = 1; i < evts.length; i++) {
        const gap = evts[i].startTime - evts[i - 1].endTime;
        if (gap > 0 && gap > maxGap) maxGap = gap;
      }
      return maxGap > 0 ? { speaker: name, ms: maxGap } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.ms - a.ms)
    .slice(0, 3);

  const exchanges = [];
  let longestExchangePair = null;
  let longestExchangeCount = 0;
  let longestExchangeMs = null;
  let currentPair = null;
  let currentCount = 0;
  let currentStart = null;
  let currentEnd = null;
  const flushExchange = () => {
    if (currentPair && currentStart && currentEnd) {
      exchanges.push({ pair: currentPair, turns: currentCount, ms: currentEnd - currentStart });
    }
    if (currentCount > longestExchangeCount) {
      longestExchangeCount = currentCount;
      longestExchangePair = currentPair;
      longestExchangeMs = (currentStart && currentEnd) ? currentEnd - currentStart : null;
    }
  };
  for (let i = 1; i < sorted.length; i++) {
    const a = sorted[i - 1].speakerName;
    const b = sorted[i].speakerName;
    if (a !== b) {
      const pairKey = [a, b].sort().join(' ↔ ');
      if (currentPair === pairKey) {
        currentCount++;
        currentEnd = sorted[i].endTime || sorted[i].startTime;
      } else {
        flushExchange();
        currentPair = pairKey;
        currentCount = 1;
        currentStart = sorted[i - 1].startTime || null;
        currentEnd = sorted[i].endTime || sorted[i].startTime || null;
      }
    }
  }
  flushExchange();

  // turns >= 2 means the pair actually bounced back at least once — a one-off
  // adjacency between two speakers isn't "back-and-forth."
  const longestExchanges = exchanges.filter(e => e.turns >= 2).sort((a, b) => b.ms - a.ms).slice(0, 3);

  return {
    longestComments,
    longestMutes,
    longestExchanges,
    longestExchange: longestExchangePair
      ? { pair: longestExchangePair, turns: longestExchangeCount, ms: longestExchangeMs }
      : null,
    longestComment: longestComments[0] || null,
    longestMute: longestMutes[0] || null,
  };
}

// ─── TIME GAP COMPRESSION ─────────────────────────────────────────────────────

export const GAP_THRESHOLD_MS = 30 * 60 * 1000;

// Shared by compressTimeGaps and computeActiveDurationMs: merged [start, end]
// dead-time intervals — explicit break entries, plus any silent gap between
// entries longer than gapThresholdMs (a paused recording resumed later —
// possibly days — on another device leaves no break entry).
function buildDeadIntervals(entries, gapThresholdMs) {
  const timed = entries.filter(e => e.startTime).sort((a, b) => a.startTime - b.startTime);
  if (timed.length === 0) return [];

  const intervals = [];
  timed.forEach(e => {
    if (e.type === 'break' && e.endTime > e.startTime) intervals.push([e.startTime, e.endTime]);
  });
  let maxEnd = timed[0].startTime;
  timed.forEach(e => {
    if (e.startTime - maxEnd > gapThresholdMs) intervals.push([maxEnd, e.startTime]);
    const end = e.endTime > e.startTime ? e.endTime : e.startTime;
    if (end > maxEnd) maxEnd = end;
  });

  intervals.sort((a, b) => a[0] - b[0]);
  const merged = [];
  intervals.forEach(([s, e]) => {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  });
  return merged;
}

// Removes dead time from entry timestamps so pauses are non-existent in the
// recording timeline. Returns a new array in the original order and length;
// break entries collapse to zero duration, untimed entries pass through
// unchanged. Idempotent.
export function compressTimeGaps(entries, gapThresholdMs = GAP_THRESHOLD_MS) {
  const timed = entries.filter(e => e.startTime);
  if (timed.length === 0) return entries.slice();

  const merged = buildDeadIntervals(entries, gapThresholdMs);

  // Clamped offset: timestamps inside a dead interval map to its start, so an
  // entry straddling a break shrinks by the overlap and order is preserved.
  const map = (t) => {
    let offset = 0;
    for (const [s, e] of merged) {
      if (t <= s) break;
      offset += Math.min(t - s, e - s);
    }
    return t - offset;
  };

  return entries.map(e => {
    if (!e.startTime) return e;
    const out = { ...e, startTime: map(e.startTime) };
    if (e.endTime) out.endTime = map(e.endTime);
    return out;
  });
}

// Real (active) meeting duration: wall-clock span from meetingStartTime to
// meetingEndTime, minus dead time — same break/silent-gap detection as
// compressTimeGaps, so this matches what the timeline already displays. A
// synthetic marker entry at meetingEndTime feeds into the same gap check,
// so a long idle stretch between the last real entry and when the meeting
// was actually ended (no break clicked) is excluded too.
export function computeActiveDurationMs(entries, meetingStartTime, meetingEndTime, gapThresholdMs = GAP_THRESHOLD_MS) {
  if (!meetingStartTime || !meetingEndTime) return 0;
  const withEndMarker = [...entries, { startTime: meetingEndTime }];
  const deadMs = buildDeadIntervals(withEndMarker, gapThresholdMs)
    .reduce((sum, [s, e]) => sum + (e - s), 0);
  return Math.max(0, (meetingEndTime - meetingStartTime) - deadMs);
}

// Duration to display for a meeting: recomputed from entries + start/end
// timestamps when available (self-heals older meetings whose stored
// totalDuration was inflated by an untracked idle gap), falling back to the
// stored field for meetings missing those timestamps.
export function getMeetingDurationMs(meeting) {
  if (meeting?.meetingStartTime && meeting?.meetingEndTime) {
    return computeActiveDurationMs(meeting.entries || [], meeting.meetingStartTime, meeting.meetingEndTime);
  }
  return meeting?.totalDuration || 0;
}

// ─── INQUIRY vs ADVOCACY ──────────────────────────────────────────────────────

export function isInquiry(entry) {
  const text = entry.text || '';
  return text.includes('/i') || text.includes('/q') || text.trimEnd().endsWith('?');
}

export function computeInquiry(entries) {
  const total = entries.length;
  const inquiryEntries = entries.filter(isInquiry);
  const advocacyEntries = entries.filter(e => !isInquiry(e));

  const overallRatio = total
    ? Math.round((inquiryEntries.length / total) * 100) / 100
    : 0;

  const bySpeaker = {};
  entries.forEach(e => {
    if (e.type === 'break' || !e.speakerName) return;
    const name = e.speakerName;
    if (!bySpeaker[name]) bySpeaker[name] = { inquiry: 0, advocacy: 0 };
    isInquiry(e) ? bySpeaker[name].inquiry++ : bySpeaker[name].advocacy++;
  });

  const perPerson = Object.entries(bySpeaker).map(([name, counts]) => {
    const tot = counts.inquiry + counts.advocacy;
    return {
      name,
      inquiry: counts.inquiry,
      advocacy: counts.advocacy,
      ratio: tot ? Math.round((counts.inquiry / tot) * 100) / 100 : 0,
    };
  });

  return {
    totalEntries: total,
    inquiryCount: inquiryEntries.length,
    advocacyCount: advocacyEntries.length,
    overallRatio,
    perPerson,
  };
}

// ─── TOPIC METRICS ────────────────────────────────────────────────────────────
// Primary path: entries have entry.topic set (via #TopicName# syntax in RecordMeeting).
// Fallback: legacy /t shortcode scanning for older meetings.

function buildTopicObject(label, section, topicEntries) {
  const ms = topicEntries.reduce((sum, e) =>
    sum + (e.endTime && e.startTime ? e.endTime - e.startTime : 0), 0);
  const speakers = new Set(topicEntries.map(e => e.speakerName)).size;
  const questions = topicEntries.filter(isInquiry).length;
  const genius = {};
  topicEntries.forEach(e => {
    extractCodes(e.text || '').forEach(code => {
      const def = ALL_CODES[code];
      if (def && def.category === 'genius') {
        genius[code] = (genius[code] || 0) + 1;
      }
    });
  });
  return { label, section, ms, speakers, questions, genius, entries: topicEntries, entryCount: topicEntries.length };
}

export function computeTopics(entries) {
  // Primary: use entry.topic field set by RecordMeeting via #TopicName# syntax
  const hasTopicField = entries.some(e => e.topic);

  if (hasTopicField) {
    const topicMap = new Map();
    entries.forEach(e => {
      if (e.type === 'break') return;
      if (!e.topic) return;
      if (!topicMap.has(e.topic)) {
        topicMap.set(e.topic, { section: e.section || null, entries: [] });
      }
      topicMap.get(e.topic).entries.push(e);
    });
    return Array.from(topicMap.entries()).map(([label, data]) =>
      buildTopicObject(label, data.section, data.entries)
    );
  }

  // Fallback: legacy /t shortcode parsing
  const topics = [];
  let currentTopic = null;
  let currentSection = null;
  let topicEntries = [];

  const flush = () => {
    if (!currentTopic) return;
    topics.push(buildTopicObject(currentTopic, currentSection, topicEntries));
    topicEntries = [];
  };

  entries.forEach(e => {
    const codes = extractCodes(e.text || '');
    if (codes.includes('/s')) {
      flush();
      const afterS = e.text.split('/s')[1] || '';
      currentSection = afterS.trim().split(/\s+/)[0] || 'New Section';
    }
    if (codes.includes('/t')) {
      flush();
      const afterT = e.text.split('/t')[1] || '';
      currentTopic = afterT.trim().split(/\s+/)[0] || 'New Topic';
    }
    if (currentTopic && e.type !== 'break') topicEntries.push(e);
  });
  flush();

  return topics;
}

// ─── QUOTES ───────────────────────────────────────────────────────────────────

export function computeQuotes(entries) {
  let productive = [];
  let unproductive = [];

  entries.forEach(e => {
    const q = parseQuotes(e.text || '');
    productive = productive.concat(q.productive.map(text => ({ text, speaker: e.speakerName })));
    unproductive = unproductive.concat(q.unproductive.map(text => ({ text, speaker: e.speakerName })));
  });

  const total = productive.length + unproductive.length;
  const ratio = total ? Math.round((productive.length / total) * 100) : null;

  return { productive, unproductive, total, ratio };
}

// Quotes for the v2 report's combined "Quotes" section — no good/bad polarity,
// just whichever Working Genius code (if any) the consultant tagged in the
// same transcript line as the quote.
export function computeTaggedQuotes(entries) {
  const quotes = [];

  entries.forEach(e => {
    const q = parseQuotes(e.text || '');
    const texts = [...q.good, ...q.bad];
    if (texts.length === 0) return;
    const geniusCode = extractCodes(e.text || '').find(code => BACKTICK_CODES[code]) || null;
    texts.forEach(text => quotes.push({ text, speaker: e.speakerName, geniusCode }));
  });

  return quotes;
}

// ─── WORKING GENIUS ───────────────────────────────────────────────────────────

export function computeGenius(entries) {
  const bySpeaker = countGeniusPerSpeaker(entries);
  const totals = {};

  Object.values(bySpeaker).forEach(codes => {
    Object.entries(codes).forEach(([code, count]) => {
      totals[code] = (totals[code] || 0) + count;
    });
  });

  const grandTotal = Object.values(totals).reduce((a, b) => a + b, 0);
  const geniusCount = Object.keys(BACKTICK_CODES).length;
  const expectedShare = grandTotal / Math.max(geniusCount, 1);

  const overuse = Object.entries(totals)
    .filter(([, v]) => v > expectedShare * 2)
    .map(([code]) => code);
  const underuse = Object.keys(BACKTICK_CODES)
    .filter(code => !totals[code] || totals[code] < expectedShare * 0.25);

  return { bySpeaker, totals, grandTotal, overuse, underuse };
}

// ─── FREQUENCY ───────────────────────────────────────────────────────────────
// Turn-share ratio: (person's turns / total turns) / (1 / N) = (turns * N) / totalTurns
// 1.0 = exactly fair share. >1 = spoke more than average (closer to center). <1 = less.

export function computeFrequency(entries) {
  const validEntries = entries.filter(e => e.speakerName);
  if (validEntries.length === 0) return [];

  const uniqueSpeakers = [...new Set(validEntries.map(e => e.speakerName))];
  const N = uniqueSpeakers.length;
  const totalTurns = validEntries.length;
  if (N === 0) return [];

  return uniqueSpeakers.map(speaker => {
    const turns = validEntries.filter(e => e.speakerName === speaker).length;
    const score = Math.round((turns * N) / totalTurns * 10) / 10;
    return { speaker, turns, score };
  });
}

// ─── COMPOSITE SCORES ─────────────────────────────────────────────────────────

export const DEFAULT_WEIGHTINGS = {
  processHealth:         1,
  participationBalance:  1,
  inquiry:               1,
  brevity:               1,
  geniusBalance:         1,
  topicDiscipline:       1,
};

export function computeScores(entries, weightings = DEFAULT_WEIGHTINGS) {
  const participation = computeParticipation(entries);
  const brevity       = computeBrevity(entries);
  const inquiry       = computeInquiry(entries);
  const genius        = computeGenius(entries);
  const topics        = computeTopics(entries);

  const participationBalanceScore = Math.round(participation.balanceIndex * 100);

  const rawInquiry = inquiry.overallRatio;
  const inquiryScore = Math.round(Math.min(rawInquiry * 2, 1) * 100);

  const brevityScore = brevity
    ? Math.max(0, Math.round(100 - (brevity.longWindedPct * 0.7) - (brevity.curtPct * 0.3)))
    : 50;

  const geniusTotal = genius.grandTotal;
  const geniusPenalty = (genius.overuse.length + genius.underuse.length) * 8;
  const geniusBalanceScore = geniusTotal > 0
    ? Math.max(0, 100 - geniusPenalty)
    : 50;

  const topicScores = topics.map(t => {
    let s = 0;
    if (t.speakers >= 2) s += 50;
    if (t.questions > 0) s += 30;
    if (t.ms > 0)        s += 20;
    return s;
  });
  const topicDisciplineScore = topics.length
    ? Math.round(topicScores.reduce((a, b) => a + b, 0) / topics.length)
    : 50;

  const silencePenalty = Math.round(participation.silenceIndex * 30);
  const processHealthScore = Math.max(0, Math.round(
    (participationBalanceScore * 0.3 + inquiryScore * 0.3 + topicDisciplineScore * 0.4) - silencePenalty
  ));

  const w = weightings;
  const wTotal = w.processHealth + w.participationBalance + w.inquiry +
                 w.brevity + w.geniusBalance + w.topicDiscipline;
  const overallScore = Math.round((
    processHealthScore        * w.processHealth +
    participationBalanceScore * w.participationBalance +
    inquiryScore              * w.inquiry +
    brevityScore              * w.brevity +
    geniusBalanceScore        * w.geniusBalance +
    topicDisciplineScore      * w.topicDiscipline
  ) / wTotal);

  return {
    processHealth:        processHealthScore,
    participationBalance: participationBalanceScore,
    inquiry:              inquiryScore,
    brevity:              brevityScore,
    geniusBalance:        geniusBalanceScore,
    topicDiscipline:      topicDisciplineScore,
    overall:              overallScore,
  };
}

// ─── MEETING INVENTORY ────────────────────────────────────────────────────────

// July 2026 revision: 6 items per group (was 10), 1:1 with the assessment
// questions in surveyQuestions.js (spN ↔ spN_q). Item wording here is the
// consultant's past-tense "what I observed" phrasing; the assessment carries
// the team's present-tense phrasing. Retired IDs (sp3–sp9, cc2, cc3, cc5–cc10,
// hb1–hb3, hb8, hb9, rg1–rg10) stay in old meetings' inventoryChecks and are
// simply not counted in new scoring. Mapping: docs/question-reduction-worksheet.md.
export const MEETING_INVENTORY = {
  compellingContent: {
    label: 'Compelling Content',
    color: '#5f7942',
    items: [
      { id: 'cc1',  text: "The organization's clarity (purpose, values, strategy, etc.) was leveraged" },
      { id: 'cc11', text: "Issues discussed genuinely needed the group's help and input" },
      { id: 'cc4',  text: 'Topics required healthy debate' },
      { id: 'cc12', text: 'Discussions led to a clear decision' },
      { id: 'cc13', text: 'Topics led to high levels of participation' },
      { id: 'cc14', text: 'Topics were discussed in the right setting' },
    ],
  },
  healthyBehaviors: {
    label: 'Healthy Behaviors',
    color: '#cba43a',
    items: [
      { id: 'hb11', text: 'The leader fostered healthy conflict' },
      { id: 'hb4',  text: 'Airtime was shared evenly' },
      { id: 'hb5',  text: 'Engagement was balanced throughout the meeting' },
      { id: 'hb6',  text: 'Inquiry over advocacy was demonstrated' },
      { id: 'hb7',  text: 'Inputs were concise and fueled dialogue' },
      { id: 'hb10', text: 'The team used levity to reduce tension and boost collaboration' },
    ],
  },
  rightGenius: {
    label: 'Right Chemistry',
    color: '#7209b7',
    items: [
      { id: 'rg11', text: 'Topics were discussed with the right context (background, data, framing)' },
      { id: 'rg12', text: 'The discussion stayed at a consistent elevation (big picture, tactical, etc.)' },
      { id: 'rg13', text: 'Inputs connected to the current discussion rather than derailing it' },
      { id: 'rg14', text: 'Frustrations were vocalized and managed' },
      { id: 'rg15', text: "Team members provoked their peers' thinking" },
      { id: 'rg16', text: 'Key moments marked inflection points in the meeting' },
    ],
  },
  simpleProcess: {
    label: 'Simple Process',
    color: '#2a3d5b',
    items: [
      { id: 'sp1',  text: 'The parts of the meeting were clear and useful' },
      { id: 'sp2',  text: 'Meeting roles (leader, facilitator, etc.) were clear and functioning' },
      { id: 'sp11', text: 'Meeting rules were clear and followed' },
      { id: 'sp12', text: 'Decisions and action steps were clearly identified' },
      { id: 'sp10', text: 'Key messages for people outside the meeting were determined' },
      { id: 'sp13', text: 'Tools (handouts, visuals, technology, etc.) were used appropriately' },
    ],
  },
};

export const SECTION_TYPES = [
  { id: 'lightning',   label: 'Lightning Round' },
  { id: 'clarity',     label: 'Clarity Review' },
  { id: 'scoreboard',  label: 'Scoreboard Review' },
  { id: 'realtime',    label: 'Real-Time Agenda' },
  { id: 'strategic',   label: 'Strategic Ad Hocs' },
  { id: 'decisions',   label: 'Decisions Review' },
  { id: 'cascading',   label: 'Cascading Messages' },
];

export function scoreInventoryFromChecks(currentChecks = {}, prevMeetings = []) {
  const groups = {};
  const recent = prevMeetings.slice(0, 4);

  Object.entries(MEETING_INVENTORY).forEach(([key, group]) => {
    let groupScore = 0;
    const itemScores = {};

    group.items.forEach(item => {
      let count = recent.filter(m => m.inventoryChecks?.[item.id] === true).length;
      if (currentChecks[item.id] === true) count++;
      itemScores[item.id] = count;
      groupScore += count;
    });

    const meetingCount = recent.length + 1;
    const groupMax = group.items.length * meetingCount;
    groups[key] = {
      label: group.label,
      score: groupScore,
      max: groupMax,
      pct: Math.round((groupScore / groupMax) * 100),
      itemScores,
    };
  });

  const total = Object.values(groups).reduce((sum, g) => sum + g.score, 0);
  const maxTotal = Object.values(groups).reduce((sum, g) => sum + g.max, 0);

  return {
    groups,
    total,
    max: maxTotal,
    pct: Math.round((total / maxTotal) * 100),
  };
}

export function scoreInventory(scores = {}) {
  const groups = {};
  let total = 0;
  let maxTotal = 0;

  Object.entries(MEETING_INVENTORY).forEach(([key, group]) => {
    const groupTotal = group.items.reduce((sum, item) => sum + (scores[item.id] || 0), 0);
    const groupMax = group.items.length * 2;
    groups[key] = {
      label: group.label,
      score: groupTotal,
      max: groupMax,
      pct: Math.round((groupTotal / groupMax) * 100),
    };
    total += groupTotal;
    maxTotal += groupMax;
  });

  return {
    groups,
    total,
    max: maxTotal,
    pct: Math.round((total / maxTotal) * 100),
  };
}

// ─── PROCESS CODES ────────────────────────────────────────────────────────────

// Returns X/Y tally for each process code base (positive / total tagged).
// e.g. { '/r': { positive: 3, negative: 1, total: 4 }, ... }
export function computeProcessCodes(entries) {
  const result = {};
  PROCESS_CODE_PAIRS.forEach(({ base }) => {
    result[base] = { positive: 0, negative: 0, total: 0 };
  });

  entries.forEach(e => {
    extractCodes(e.text || '').forEach(code => {
      const def = ALL_CODES[code];
      if (!def || def.category !== 'process') return;
      const baseCode = def.positive ? code : def.pairCode;
      if (!result[baseCode]) return;
      if (def.positive) result[baseCode].positive++;
      else result[baseCode].negative++;
      result[baseCode].total++;
    });
  });

  return result;
}

// Per-genius-code list of occurrence-minute offsets (relative to the meeting's
// first entry). Used for "chemistry over time" timeline charts.
// e.g. { '`w': [3, 14, 34], '`i': [6, 16, 22], ... }
export function computeGeniusTimeline(entries) {
  const timeline = {};
  Object.keys(BACKTICK_CODES).forEach(code => { timeline[code] = []; });

  const sorted = entries
    .filter(e => e.type !== 'break' && e.startTime)
    .sort((a, b) => a.startTime - b.startTime);
  if (!sorted.length) return timeline;

  const start = sorted[0].startTime;
  sorted.forEach(e => {
    extractCodes(e.text || '').forEach(code => {
      if (!timeline[code]) return;
      timeline[code].push(Math.round((e.startTime - start) / 60000));
    });
  });

  return timeline;
}

// Count of entries containing any of the given shortcodes (e.g. ['/h'] for
// "Asks for Help", or the org-clarity codes ['/1',...,'/6']).
export function countTagOccurrences(entries, tags) {
  const tagSet = new Set(tags);
  let count = 0;
  entries.forEach(e => {
    extractCodes(e.text || '').forEach(code => {
      if (tagSet.has(code)) count++;
    });
  });
  return count;
}

// ─── FULL REPORT DATA ─────────────────────────────────────────────────────────

export function buildReportData(entries, inventoryScores = {}, weightings = DEFAULT_WEIGHTINGS) {
  entries = compressTimeGaps(entries);
  return {
    participation: computeParticipation(entries),
    brevity:       computeBrevity(entries),
    awards:        computeAwards(entries),
    inquiry:       computeInquiry(entries),
    topics:        computeTopics(entries),
    quotes:        computeQuotes(entries),
    taggedQuotes:  computeTaggedQuotes(entries),
    genius:        computeGenius(entries),
    frequency:     computeFrequency(entries),
    scores:        computeScores(entries, weightings),
    inventory:     scoreInventory(inventoryScores),
    processCodes:  computeProcessCodes(entries),
  };
}
